import React, { useState, useEffect } from 'react';
import { Map } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

export default function PartyCamps({ categories }) {
  const [campsData, setCampsData] = useState({ running: [], upcoming: [], past: [] });
  const [activeTab, setActiveTab] = useState('running');
  const [loading, setLoading] = useState(true);

  const fetchCamps = async () => {
    try {
      setLoading(true);
      const headers = {
        'Client-ID': 'lokanatya_client',
        'Client-Secret': 'super_secret_key_123'
      };

      // We purposefully do NOT filter by selected_dist_id here, 
      // because we want to show Party Camps "Across Odisha" at all times!

      const res = await axios.get(`/api/fetch_camps`, { headers });
      if (res.data) {
        setCampsData(res.data);
      }
    } catch (error) {
      console.error('Error fetching camps:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCamps();
    window.addEventListener('locationChanged', fetchCamps);
    return () => window.removeEventListener('locationChanged', fetchCamps);
  }, []);

  const currentCamps = campsData[activeTab] || [];
  const displayCamps = currentCamps.slice(0, 4);
  const totalCampsCount = (campsData.running?.length || 0) + (campsData.upcoming?.length || 0) + (campsData.past?.length || 0);

  if (!loading && totalCampsCount === 0) {
    return null;
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-3 mb-3">
      <div className="text-start mb-6">
        <span className="bg-red-50 text-red-500 font-bold text-[10px] tracking-widest uppercase px-4 py-1.5 rounded-full">Across Odisha</span>
        <h2 className="text-xl md:text-3xl font-bold text-gray-900 mt-3 mb-1">Party Camp Locations</h2>
      </div>

      <div className="flex justify-between items-center mb-2 md:mb-4">
        <div className="flex space-x-2 md:space-x-3 overflow-x-auto hide-scrollbar">
          <button onClick={() => setActiveTab('running')} className={`${activeTab === 'running' ? 'bg-[#19479a] text-white' : 'bg-white text-gray-600 border border-gray-200'} font-medium px-4 md:px-6 py-1.5 rounded-full text-sm shadow-sm hover:bg-[#153a80] hover:text-white transition shrink-0`}>Running</button>
          <button onClick={() => setActiveTab('upcoming')} className={`${activeTab === 'upcoming' ? 'bg-[#19479a] text-white' : 'bg-white text-gray-600 border border-gray-200'} font-medium px-4 md:px-6 py-1.5 rounded-full text-sm shadow-sm hover:bg-[#153a80] hover:text-white transition shrink-0`}>Upcoming</button>
          <button onClick={() => setActiveTab('past')} className={`${activeTab === 'past' ? 'bg-[#19479a] text-white' : 'bg-white text-gray-600 border border-gray-200'} font-medium px-4 md:px-6 py-1.5 rounded-full text-sm shadow-sm hover:bg-[#153a80] hover:text-white transition shrink-0`}>Past</button>
        </div>
        <button className="hidden md:flex text-gray-500 hover:text-gray-800 text-xs font-medium border border-gray-200 px-4 py-1.5 rounded-full items-center bg-white transition hover:bg-gray-50 shrink-0">
          View All &rarr;
        </button>
      </div>

      <motion.div
        key={activeTab + displayCamps.length}
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[0.4rem] md:gap-5"
      >
        {displayCamps.length === 0 && !loading && (
          <div className="col-span-full text-center text-gray-500 py-8">No camps available.</div>
        )}
        {displayCamps.map((camp, index) => {
          // Find the category (jatra party) for this camp
          const jatra = categories ? categories.find(c => c.id === camp.cat_id) : null;

          // Format dates
          const formatCampDate = (dateString) => {
            if (!dateString) return 'NA';
            const d = new Date(dateString);
            return isNaN(d) ? dateString : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
          };
          const dateRange = `${formatCampDate(camp.camp_from)} - ${formatCampDate(camp.camp_to)}`;

          return (
            <motion.div variants={itemVariants} key={index} className="flex bg-white rounded-xl h-[96px] border border-gray-200 group hover:border-[#1d4ed8]/30 hover:shadow-lg transition-all duration-300 relative">
              <div className="w-24 md:w-28 h-full bg-[#e5e7eb] shrink-0 relative overflow-hidden rounded-l-xl">
                <img src={camp.camp_image || jatra?.cat_image || 'https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg'} alt={camp.cat_name || jatra?.cat_name || 'Jatra'} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute top-1.5 right-1.5 bg-white/90 backdrop-blur-sm text-green-600 text-[8px] font-extrabold px-1.5 py-0.5 rounded shadow-sm tracking-wider uppercase">{activeTab}</div>
              </div>
              <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
                <div className="flex flex-col">
                  <div className="flex items-start justify-between mb-1">
                    <div className="relative group/title flex-1 min-w-0 pr-2">
                      <h4 className="text-sm font-bold text-gray-900 truncate leading-tight group-hover/title:text-[#1d4ed8] transition-colors cursor-pointer">
                        {camp.cat_name || jatra?.cat_name || 'Camp'}
                      </h4>
                      {/* Custom Tooltip for Title */}
                      <div className="absolute bottom-full left-0 mb-2 hidden group-hover/title:block bg-gray-900 text-white px-2.5 py-1.5 rounded shadow-xl z-50 w-max max-w-[200px] whitespace-normal pointer-events-none opacity-0 group-hover/title:opacity-100 transition-opacity duration-200">
                        {camp.cat_name || jatra?.cat_name || 'Camp'}
                        {/* Tooltip arrow */}
                        <div className="absolute -bottom-1 left-4 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                      </div>
                    </div>
                    <a href={camp.latitude && camp.longitude ? `https://www.google.com/maps/dir/?api=1&destination=${camp.latitude},${camp.longitude}` : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(camp.camp_location || 'Odisha')}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[#1d4ed8] bg-blue-50 px-2 py-1 rounded-md text-[9px] font-bold hover:bg-[#1d4ed8] hover:text-white transition-colors shrink-0">
                      <Map size={10} strokeWidth={2.5} /> Direction
                    </a>
                  </div>
                  <span className="text-gray-500 text-[10px] font-medium flex items-center">
                    <span className="mr-1.5 text-xs">📅</span> {dateRange}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-auto pt-2 border-t border-gray-50">
                  <div className="flex items-center text-gray-700 text-[10px] font-medium w-full relative group/loc cursor-pointer">
                    <span className="mr-1.5 text-xs text-[#ef2b2d] shrink-0">📍</span>
                    <span className="truncate flex-1 min-w-0">{camp.camp_location || camp.district_name || 'Odisha'}</span>

                    {/* Custom Tooltip */}
                    <div className="absolute bottom-full left-4 mb-2 hidden group-hover/loc:block bg-gray-900 text-white px-2.5 py-1.5 rounded shadow-xl z-50 w-max max-w-[200px] whitespace-normal pointer-events-none opacity-0 group-hover/loc:opacity-100 transition-opacity duration-200">
                      {camp.camp_location || camp.district_name || 'Odisha'}
                      {/* Tooltip arrow */}
                      <div className="absolute -bottom-1 left-4 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )
        })}
      </motion.div>
    </div>
  );
}
